import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { useAuth } from '../AuthProvider';
import { usePermissions } from '../rbac/PermissionsProvider';
import { PERMISSIONS } from '../rbac/permissions';
import SearchInput from '../components/SearchInput';
import { formatCurrency } from '../util';
import { useMsal } from '@azure/msal-react';
import {
    COLORS, FONT, TH_SX, DataRow, PageShell,
    StickyFilterBar, SubtotalRow, TotalRow, fadeUp,
} from '../shared/DesignSystem';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Grid, IconButton, Paper, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, Tooltip, Typography, Alert,
} from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getApiToken } from '../authUtils';
import CostSheetHistory from '../components/CostSheetHistory';
import { ItemsContext } from '../App';

const urlconfig = require('../config/urlconfig');

// ─── Constants ───────────────────────────────────────────────────────────────

const REQUIRED_APPROVERS = ['Rahul Rasa', 'Sanjeev Bandari', 'Vamsi Perugu'];

// ─── Derived helpers ──────────────────────────────────────────────────────────

const getApprovalState = (item) => {
    const approvedBy = item.approvedBy || [];
    const isRejected = item.pending === 'false';
    const isFullyApproved = REQUIRED_APPROVERS.every(a => approvedBy.includes(a));
    const approvedCount = REQUIRED_APPROVERS.filter(a => approvedBy.includes(a)).length;
    if (isRejected) return { key: 'rejected', label: 'Rejected', approvedCount };
    if (isFullyApproved) return { key: 'approved', label: 'Fully Approved', approvedCount };
    if (approvedCount > 0) return { key: 'inProgress', label: `In Progress (${approvedCount}/${REQUIRED_APPROVERS.length})`, approvedCount };
    return { key: 'pending', label: 'Pending', approvedCount };
};

// ─── Styled components ────────────────────────────────────────────────────────

const DashHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '20px 24px 16px',
    background: COLORS.white,
    borderBottom: `1px solid ${COLORS.borderAlt}`,
});

const KpiCard = styled(Paper)(({ active }) => ({
    borderRadius: 16,
    padding: '16px 20px',
    background: COLORS.white,
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform .15s, box-shadow .15s',
    border: `2px solid ${active ? 'currentColor' : 'transparent'}`,
    animation: `${fadeUp} .35s ease both`,
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.10)' },
    userSelect: 'none',
}));

const ApprovalChip = ({ state }) => {
    const config = {
        approved:   { color: COLORS.success,  bg: COLORS.successBg,  label: state.label },
        inProgress: { color: '#d97706',        bg: '#fef3c7',         label: state.label },
        rejected:   { color: COLORS.danger,   bg: COLORS.dangerBg,   label: state.label },
        pending:    { color: COLORS.textMuted, bg: COLORS.surface,    label: 'Pending' },
    }[state.key] || { color: COLORS.textMuted, bg: COLORS.surface, label: state.label };

    return (
        <Chip
            label={config.label}
            size="small"
            sx={{
                fontSize: 11, fontWeight: 700,
                color: config.color,
                background: config.bg,
                border: `1px solid ${config.color}33`,
            }}
        />
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ApprovalDashboard = () => {
    const { username } = useAuth();
    const { instance, accounts } = useMsal();
    const account = accounts?.[0] ?? null;
    const loggedInUser = account?.name || '';

    // Approval-specific data state — decoupled from shared ItemsContext
    const [summaryPage, setSummaryPage]     = useState({ content: [], totalElements: 0 });
    const [localLoading, setLocalLoading]   = useState(false);
    const [localError, setLocalError]       = useState(null);

    const [selectedItem, setSelectedItem]   = useState(null);
    const [showModal, setShowModal]         = useState(false);
    const [modalLoading, setModalLoading]   = useState(false);
    const [searchQuery, setSearchQuery]     = useState('');
    const [remarksModal, setRemarkModal]    = useState(false);
    const [remarkText, setRemarkText]       = useState('');
    const [btnSpinner, setBtnSpinner]       = useState(false);
    const [activeCard, setActiveCard]       = useState('all');
    const [page, setPage]                   = useState(0);
    const [rowsPerPage, setRowsPerPage]     = useState(10);

    const { can } = usePermissions();
    const canApprove = can(PERMISSIONS.COSTSHEET_APPROVE);

    const [isFetchingAll, setIsFetchingAll] = useState(true);
    const isFirstRunRef    = useRef(true);
    const isFetchingAllRef = useRef(false);
    const latestSearchRef  = useRef(searchQuery);
    const {legalVendors, legalClients, refreshLegalClientsAndVendors}= useContext(ItemsContext)

    // ── Data fetching ─────────────────────────────────────────────────────────

    const fetchApprovalSummary = useCallback(async (search, pg, size) => {
        // In E2E, useMsal() has no accounts but getApiToken returns a fake token — skip the guard.
        if (!account && !window.__E2E_TEST__) return null;
        const accessToken = await getApiToken(instance, account);
        const url = `${urlconfig.backend_api_base_url}/api/v2/items/approval-summary?search=${encodeURIComponent(search)}&page=${pg}&size=${size}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
        if (!res.ok) throw new Error(`Failed to fetch approval data: ${res.status}`);
        return res.json();
    }, [instance, account]);

    // Phase 1: probe with size=1 to discover totalElements cheaply.
    useEffect(() => {
        latestSearchRef.current = searchQuery;
        isFetchingAllRef.current = false;
        setIsFetchingAll(true);
        setLocalError(null);
        setLocalLoading(true);
        fetchApprovalSummary(searchQuery, 0, 1)
            .then(data => { if (data) setSummaryPage(data); })
            .catch(err => setLocalError(err.message))
            .finally(() => setLocalLoading(false));
    }, [searchQuery, fetchApprovalSummary]);

    // Phase 2: fires whenever summaryPage changes (each API response lands).
    // Skips the very first run (before Phase 1 has returned anything),
    // then fetches all rows once totalElements is known.
    useEffect(() => {
        if (isFirstRunRef.current) { isFirstRunRef.current = false; return; }
        if (isFetchingAllRef.current) { setIsFetchingAll(false); return; }
        const total = summaryPage.totalElements ?? 0;
        if (total === 0 || (summaryPage.content?.length ?? 0) >= total) {
            setIsFetchingAll(false);
            return;
        }
        isFetchingAllRef.current = true;
        setLocalLoading(true);
        fetchApprovalSummary(latestSearchRef.current, 0, total)
            .then(data => { if (data) setSummaryPage(data); })
            .catch(err => setLocalError(err.message))
            .finally(() => setLocalLoading(false));
    }, [summaryPage]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
            refreshLegalClientsAndVendors();
        }, [refreshLegalClientsAndVendors]);

    // ── Derived data ──────────────────────────────────────────────────────────

    const allItems = useMemo(() => summaryPage.content || [], [summaryPage.content]);

    const filteredItems = useMemo(() => {
        return allItems.filter(item => {
            const state = getApprovalState(item);
            if (activeCard === 'all')        return true;
            if (activeCard === 'pending')    return state.key === 'pending';
            if (activeCard === 'inProgress') return state.key === 'inProgress';
            if (activeCard === 'approved')   return state.key === 'approved';
            if (activeCard === 'rejected')   return state.key === 'rejected';
            if (activeCard === 'pending_me') {
                const approvedBy = item.approvedBy || [];
                return REQUIRED_APPROVERS.includes(loggedInUser)
                    && !approvedBy.includes(loggedInUser)
                    && item.pending !== 'false';
            }
            return true;
        });
    }, [allItems, activeCard, loggedInUser]);

    const paginatedItems = useMemo(
        () => filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredItems, page, rowsPerPage]
    );

    const kpiCounts = useMemo(() => ({
        all:        allItems.length,
        pending:    allItems.filter(i => getApprovalState(i).key === 'pending').length,
        inProgress: allItems.filter(i => getApprovalState(i).key === 'inProgress').length,
        approved:   allItems.filter(i => getApprovalState(i).key === 'approved').length,
        rejected:   allItems.filter(i => getApprovalState(i).key === 'rejected').length,
        pending_me: allItems.filter(i => {
            const approvedBy = i.approvedBy || [];
            return REQUIRED_APPROVERS.includes(loggedInUser)
                && !approvedBy.includes(loggedInUser)
                && i.pending !== 'false';
        }).length,
    }), [allItems, loggedInUser]);

    const kpiCards = [
        { key: 'all',        label: 'All',            icon: '📋', color: COLORS.indigo,  bg: COLORS.indigoBg  },
        { key: 'pending',    label: 'Pending',         icon: '⏳', color: '#d97706',      bg: '#fef3c7'        },
        { key: 'inProgress', label: 'In Progress',     icon: '🔄', color: '#2563eb',      bg: '#eff6ff'        },
        { key: 'approved',   label: 'Approved',        icon: '✅', color: COLORS.success, bg: COLORS.successBg },
        { key: 'rejected',   label: 'Rejected',        icon: '❌', color: COLORS.danger,  bg: COLORS.dangerBg  },
        { key: 'pending_me', label: 'Pending with Me', icon: '🙋', color: '#7c3aed',      bg: '#f5f3ff'        },
    ];

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleRowClick = useCallback(async (summaryItem) => {
        setSelectedItem(summaryItem);
        setShowModal(true);
        setModalLoading(true);
        try {
            const accessToken = await getApiToken(instance, account);
            const res = await fetch(
                `${urlconfig.backend_api_base_url}/api/v2/items/${summaryItem.id}`,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            // if (res.ok) setSelectedItem(await res.json());
            if (res.ok) {
            const fullItem = await res.json();
            // ── Flatten costSummary GST fields into the item for easy access ──
            const cs = fullItem.costSummary || {};
            setSelectedItem({
                ...fullItem,
                transportationGST: cs.transportationGST ?? 0,
                customizationGST:  cs.customizationGST  ?? 0,
                marketingGST:      cs.marketingGST       ?? 0,
            });
        }
        } catch (err) {
            console.error('Failed to load item detail:', err);
        } finally {
            setModalLoading(false);
        }
    }, [instance, account]);

    const handleSearch = (query) => {
        setSearchQuery(query);
        setPage(0);
    };

    const handleCardClick = (key) => {
        setActiveCard(key);
        setPage(0);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedItem(null);
    };

    const sendAuthenticatedRequest = async (url, method, body = null) => {
        const accessToken = await getApiToken(instance, account);
        const options = {
            method,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        };
        if (body) options.body = JSON.stringify(body);
        const res = await fetch(url, options);
        if (!res.ok) {
            const text = await res.text();
            const err = text ? JSON.parse(text) : { message: 'Empty response' };
            throw new Error(`API Error: ${res.status} ${res.statusText} - ${err.message || 'Unknown error'}`);
        }
        const ct = res.headers.get('content-type');
        return ct?.includes('application/json') ? res.json() : res.text();
    };

    const sendCostSheetApprovalEmail = async () => {
        if (!selectedItem) return;
        try {
            await sendAuthenticatedRequest(
                `${urlconfig.backend_api_base_url}/api/v2/send-approval-email`,
                'POST',
                {
                    subject: `A cost sheet approval status has been updated by ${username}.`,
                    salesOrderNo: selectedItem.salesOrderNo,
                    createdBy: selectedItem.createdBy,
                    userEmail: selectedItem.userEmail?.trim() || null,
                }
            );
        } catch (err) {
            console.error('Failed to send approval email:', err);
        }
    };

    const handleAccountEmail = async (item) => {
        const approved = item.approvedBy || [];
        if (!REQUIRED_APPROVERS.every(p => approved.includes(p))) return;
        try {
            await sendAuthenticatedRequest(
                `${urlconfig.backend_api_base_url}/api/v2/send-email-accounts`,
                'POST',
                { salesOrderNo: item.salesOrderNo, createdBy: item.createdBy, approvedBy: approved }
            );
        } catch (err) {
            console.error('Email send error:', err);
        }
    };

    const handleApproval = async (approved) => {
        if (!selectedItem) return;
        setBtnSpinner(true);
        await sendCostSheetApprovalEmail();
        try {
            // The server records the decision under the signed-in user's own name.
            const updatedItem = await sendAuthenticatedRequest(
                `${urlconfig.backend_api_base_url}/api/v2/items/${selectedItem.id}/approval`,
                'POST',
                { approved, remarks: remarkText }
            );
            await handleAccountEmail({ ...selectedItem, approvedBy: updatedItem?.approvedBy || [] });
            // Refresh list — fetch all with known total, let Phase 2 finalise
            isFetchingAllRef.current = true;
            setLocalLoading(true);
            fetchApprovalSummary(latestSearchRef.current, 0, summaryPage.totalElements || 1)
                .then(data => { if (data) setSummaryPage(data); })
                .catch(err => console.error('Refresh failed:', err))
                .finally(() => setLocalLoading(false));
        } catch (err) {
            console.error('Error updating status:', err);
            alert(`Failed to update status: ${err.message || 'Unknown error'}`);
        } finally {
            setBtnSpinner(false);
            setRemarkText('');
            setRemarkModal(false);
            setShowModal(false);
        }
    };

    const getRowColor = (item) => {
        const customPP = (item.customRows || []).reduce((t, r) => t + (Number(r.customTotalPp) || 0), 0);
        const customSP = (item.customRows || []).reduce((t, r) => t + (Number(r.customTotalSp) || 0), 0);

        const totalPP = (item.items?.reduce((a, s) => a + s.qty * (s.pp * (1 + (s.gst || 0) / 100)), 0) || 0)
            + (item.transportationTotalPp || 0) + (item.customizationTotalPp || 0) + (item.marketingTotalPp || 0) + customPP;
        const totalSP = (item.items?.reduce((a, s) => a + s.qty * (s.sp * (1 + (s.gst || 0) / 100)), 0) || 0)
            + (item.transportationTotalSp || 0) + (item.customizationTotalSp || 0) + (item.marketingTotalSp || 0) + customSP;
        const pct = totalPP !== 0 ? ((totalSP - totalPP) / totalPP) * 100 : 0;
        if (pct < 12) return '#fff5f5';
        if (pct < 20) return '#fffbeb';
        return '#f0fdf4';
    };

    // ── Loading / error guards ────────────────────────────────────────────────

    if (localError) {
        return <Alert severity="error" sx={{ m: 4, borderRadius: 3 }}>{localError}</Alert>;
    }

    // True only while the first fetch is in-flight — keeps SearchInput mounted even when empty
    const isInitialLoading = !summaryPage.content.length && (isFetchingAll || localLoading);

    const isInternal = selectedItem?.costsheetfor?.toLowerCase() === 'internal';

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <PageShell>
            {/* ── Header ─────────────────────────────────────────────────── */}
            <DashHeader>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight={900} letterSpacing="-0.5px" fontFamily={FONT.display}>
                        Approval Dashboard
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Logged in as <strong>{username}</strong>
                    </Typography>
                </Box>
            </DashHeader>

            {/* ── Sticky filter bar ───────────────────────────────────────── */}
            <StickyFilterBar elevation={0}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <SearchInput
                        onSearch={handleSearch}
                        placeholder="Search by client, order no, created by or sales SPOC…"
                        initialValue={searchQuery}
                    />
                    {activeCard !== 'all' && (() => {
                        const activeKpi = kpiCards.find(c => c.key === activeCard);
                        return (
                            <Chip
                                label={activeKpi?.label || activeCard}
                                onDelete={() => handleCardClick('all')}
                                size="small"
                                sx={{
                                    fontSize: 12, fontWeight: 700,
                                    color: activeKpi?.color || COLORS.indigo,
                                    background: activeKpi?.bg || COLORS.indigoBg,
                                    border: `1px solid ${activeKpi?.color || COLORS.indigo}33`,
                                    '& .MuiChip-deleteIcon': { color: activeKpi?.color || COLORS.indigo },
                                }}
                            />
                        );
                    })()}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        Showing <strong>{filteredItems.length}</strong> of <strong>{allItems.length}</strong> cost sheets
                    </Typography>
                </Box>
            </StickyFilterBar>

            <Box sx={{ p: { xs: 2, md: 3 } }}>
                {isInitialLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: 2 }}>
                        <CircularProgress size={48} thickness={3} sx={{ color: COLORS.indigo }} />
                        <Typography color="text.secondary" fontWeight={600} fontFamily={FONT.display}>Loading…</Typography>
                    </Box>
                ) : (<>
                {/* ── KPI cards ──────────────────────────────────────────── */}
                <Box data-testid="kpi-filters" sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                        {kpiCards.map((c, i) => (
                            <Grid item xs={6} sm={4} md={2} key={c.key}>
                                <KpiCard
                                    active={activeCard === c.key ? 1 : 0}
                                    elevation={activeCard === c.key ? 3 : 1}
                                    onClick={() => handleCardClick(c.key)}
                                    sx={{
                                        animationDelay: `${i * 50}ms`,
                                        color: c.color,
                                        boxShadow: activeCard === c.key ? `0 6px 20px ${c.color}30` : '0 2px 12px rgba(0,0,0,0.06)',
                                        borderColor: activeCard === c.key ? c.color : 'transparent',
                                    }}
                                >
                                    <Box sx={{ position: 'absolute', right: -8, top: -8, width: 56, height: 56, borderRadius: '50%', background: c.bg, opacity: 0.6 }} />
                                    <Typography sx={{ fontSize: 22, lineHeight: 1, mb: 0.5 }}>{c.icon}</Typography>
                                    <Typography variant="h5" fontWeight={800} fontFamily={FONT.display} color={c.color}>
                                        {kpiCounts[c.key]}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>{c.label}</Typography>
                                </KpiCard>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* ── Table ──────────────────────────────────────────────── */}
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ borderRadius: 3, border: `1px solid ${COLORS.borderAlt}`, overflowX: 'auto' }}
                >
                    <Table sx={{ minWidth: 1000 }} aria-label="Approval Dashboard Table">
                        <TableHead>
                            <TableRow>
                                {['Costsheet For', 'Client Name', 'Sales Order No', 'Sales SPOC', 'SCM SPOC', 'Details', 'Created By', 'Approval Status', 'Actions'].map(col => (
                                    <TableCell key={col} sx={TH_SX}>{col}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(localLoading || isFetchingAll) ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={32} sx={{ color: COLORS.indigo }} />
                                    </TableCell>
                                </TableRow>
                            ) : filteredItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary" fontWeight={600}>No cost sheets found.</Typography>
                                        <Typography variant="caption" color="text.disabled">Try a different search or filter.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedItems.map((item, idx) => {
                                    const state = getApprovalState(item);
                                    const transLoss = (item.transportationTotalSp || 0) < (item.transportationTotalPp || 0);
                                    const custLoss  = (item.customizationTotalSp  || 0) < (item.customizationTotalPp  || 0);
                                    const mktLoss   = (item.marketingTotalSp      || 0) < (item.marketingTotalPp      || 0);
                                    const tooltipLines = [];
                                    if (transLoss) tooltipLines.push('Transportation Loss');
                                    if (custLoss)  tooltipLines.push('Customization Loss');
                                    if (mktLoss)   tooltipLines.push('Marketing Loss');

                                    return (
                                        <DataRow
                                            key={item.id}
                                            rowindex={idx}
                                            onClick={() => handleRowClick(item)}
                                            sx={{ backgroundColor: getRowColor(item) }}
                                        >
                                            <TableCell>{item.costsheetfor || '—'}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{item.clientRegistrations?.[0]?.legalClientName || item.clientName}</TableCell>
                                            <TableCell sx={{ fontFamily: FONT.mono, fontSize: 12 }}>{item.salesOrderNo}</TableCell>
                                            <TableCell>{item.salesPOC}</TableCell>
                                            <TableCell>{item.scmspoc}</TableCell>
                                            <TableCell sx={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.items?.map(s => s.details).join(', ')}
                                            </TableCell>
                                            <TableCell>{item.createdBy}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <ApprovalChip state={state} />
                                                    {state.key === 'rejected' && item.remarks && (
                                                        <Typography variant="caption" color="error" fontWeight={600}>
                                                            Note: {item.remarks}
                                                        </Typography>
                                                    )}
                                                    {(state.key === 'inProgress' || state.key === 'approved') && (item.approvedBy || []).length > 0 && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(item.approvedBy || []).join(', ')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell onClick={e => e.stopPropagation()}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => handleRowClick(item)}
                                                        sx={{ borderRadius: 1.5, fontSize: 12, textTransform: 'none', bgcolor: COLORS.indigo, '&:hover': { bgcolor: COLORS.navy } }}
                                                    >
                                                        View
                                                    </Button>
                                                    {tooltipLines.length > 0 && (
                                                        <Tooltip title={tooltipLines.join(' · ')} arrow>
                                                            <IconButton size="small">
                                                                <InfoOutlinedIcon fontSize="small" color="action" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </DataRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={filteredItems.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{ borderTop: `1px solid ${COLORS.borderAlt}` }}
                    />
                </TableContainer>
            </>)}
            </Box>

            {/* ── Cost sheet detail modal ──────────────────────────────────── */}
            <Dialog
                open={showModal}
                onClose={handleCloseModal}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: `1px solid ${COLORS.borderAlt}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography fontWeight={800} variant="h6" fontFamily={FONT.display}>
                            Sales Order No: {selectedItem?.salesOrderNo || 'N/A'}
                        </Typography>
                        {selectedItem && <ApprovalChip state={getApprovalState(selectedItem)} />}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CostSheetHistory selectedItem={selectedItem} />
                        <IconButton size="small" onClick={handleCloseModal} aria-label="Close">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 0, bgcolor: COLORS.surface }}>
                    {selectedItem ? (
                        <Box sx={{ p: 3 }}>
                            {/* Meta info */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                {[
                                    ['Cost Sheet For', selectedItem.costsheetfor],
                                    ['Order Status', selectedItem.orderStatus],
                                    ['Cost Sheet Remarks', selectedItem.costSheetRemarks],
                                    ...(isInternal ? [['Internal Category', selectedItem.internalCategory]] : []),
                                ].map(([label, value]) => (
                                    <Grid item xs={12} sm={4} key={label}>
                                        <Paper variant="outlined" sx={{ p: '10px 14px', borderRadius: 2 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing="0.05em">
                                                {label}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600} mt={0.25}>{value || 'N/A'}</Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Cost sheet table */}
                            <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase" letterSpacing="0.05em" color={COLORS.indigo} mb={1.5}>
                                Cost Sheet
                            </Typography>
                            {modalLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                                    <CircularProgress size={36} sx={{ color: COLORS.indigo }} />
                                </Box>
                            ) : null}
                            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto', border: `1px solid ${COLORS.borderAlt}`, borderRadius: 2, display: modalLoading ? 'none' : 'block' }}>
                                <Table sx={{ minWidth: 1200 }} size="small">
                                    <TableHead>
                                        <TableRow sx={{ background: COLORS.surface }}>
                                            <TableCell colSpan={4} sx={{ ...TH_SX, fontWeight: 600, textTransform: 'none', fontSize: 12 }}>
                                                Date: {selectedItem.date ? new Date(selectedItem.date).toLocaleDateString('en-GB') : 'N/A'}
                                            </TableCell>
                                            <TableCell colSpan={4} sx={{ ...TH_SX, fontWeight: 600, textTransform: 'none', fontSize: 12 }}>
                                                Delivery: {selectedItem.deliveryDate ? new Date(selectedItem.deliveryDate).toLocaleDateString('en-GB') : 'N/A'}
                                            </TableCell>
                                            <TableCell colSpan={10} sx={{ ...TH_SX, fontWeight: 600, textTransform: 'none', fontSize: 12 }}>
                                                Created By: {selectedItem.createdBy || 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ background: COLORS.surface }}>
                                            <TableCell colSpan={8} sx={{ ...TH_SX, fontWeight: 600, textTransform: 'none', fontSize: 12 }}>
                                                Client: {selectedItem.clientRegistrations?.[0]?.legalClientName || selectedItem.clientName || 'N/A'}
                                            </TableCell>
                                            <TableCell colSpan={10} sx={{ ...TH_SX, fontWeight: 600, textTransform: 'none', fontSize: 12 }}>
                                                Sales Order No: {selectedItem.salesOrderNo || 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ background: COLORS.surface }}>
                                            <TableCell colSpan={4} sx={{ ...TH_SX, fontWeight: 600, textTransform: 'none', fontSize: 12 }}>Sales SPOC: {selectedItem.salesPOC || 'N/A'}</TableCell>
                                            <TableCell colSpan={5} sx={{ ...TH_SX, fontWeight: 600, textTransform: 'none', fontSize: 12 }}>SCM SPOC: {selectedItem.scmspoc || 'N/A'}</TableCell>
                                            <TableCell colSpan={4} sx={{ ...TH_SX, fontWeight: 600, textTransform: 'none', fontSize: 12 }}>Payment Terms: {selectedItem.clientPaymentTerms || 'N/A'}</TableCell>
                                            <TableCell colSpan={5} sx={{ ...TH_SX, fontWeight: 600, textTransform: 'none', fontSize: 12 }}>Location: {selectedItem.location || 'N/A'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            {['Details','Brand','Category','Sub-Category','Qty','GST','PP (excl GST)','PP (incl GST)','SP (excl GST)','SP (incl GST)','Total PP (excl)','Total SP (excl)','Margin','Margin %','Total PP (incl)','Total SP (incl)','Vendor','Payment Terms'].map(h => (
                                                <TableCell key={h} sx={TH_SX}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedItem.items?.length > 0 ? selectedItem.items.map((si, i) => {
                                            const itemTotalPp = (si.qty || 0) * (si.pp || 0);
                                            const itemTotalSp = (si.qty || 0) * (si.sp || 0);
                                            const itemMarginPct = itemTotalPp ? ((itemTotalSp - itemTotalPp) / itemTotalPp * 100).toFixed(1) : '0.0';
                                            return (
                                                <DataRow key={i} rowindex={i}>
                                                    <TableCell sx={{ maxWidth: 120, wordWrap: 'break-word', whiteSpace: 'normal' }}>{si.details || 'N/A'}</TableCell>
                                                    <TableCell>{si.brand?.name || 'N/A'}</TableCell>
                                                    <TableCell>{si.category?.name || 'N/A'}</TableCell>
                                                    <TableCell>{si.subcategory?.name || 'N/A'}</TableCell>
                                                    <TableCell>{si.qty || 0}</TableCell>
                                                    <TableCell>{si.gst || 0}%</TableCell>
                                                    <TableCell>{formatCurrency(si.pp || 0)}</TableCell>
                                                    <TableCell>{formatCurrency((si.pp || 0) * (1 + (si.gst || 0) / 100))}</TableCell>
                                                    <TableCell>{formatCurrency(si.sp || 0)}</TableCell>
                                                    <TableCell>{formatCurrency((si.sp || 0) * (1 + (si.gst || 0) / 100))}</TableCell>
                                                    <TableCell>{formatCurrency(itemTotalPp)}</TableCell>
                                                    <TableCell>{formatCurrency(itemTotalSp)}</TableCell>
                                                    <TableCell>{formatCurrency(itemTotalSp - itemTotalPp)}</TableCell>
                                                    <TableCell>{itemMarginPct} %</TableCell>
                                                    <TableCell>{formatCurrency((si.qty || 0) * ((si.pp || 0) * (1 + (si.gst || 0) / 100)))}</TableCell>
                                                    <TableCell>{formatCurrency((si.qty || 0) * ((si.sp || 0) * (1 + (si.gst || 0) / 100)))}</TableCell>
                                                    <TableCell>{legalVendors.find(v => Number(v.id) === Number(si.legalVendorId))?.legalVendorName || si.vendorName || "N/A"}</TableCell>
                                                    <TableCell>{si.vendorPaymentTerms || 'N/A'}</TableCell>
                                                </DataRow>
                                            );
                                        }) : (
                                            <TableRow><TableCell colSpan={18} align="center">No items in this order.</TableCell></TableRow>
                                        )}

                                         {/* ── CUSTOM ROWS ── */}
                                        {(selectedItem.customRows || []).map((row, i) => {
                                            const pp = Number(row.customTotalPp) || 0;
                                            const sp = Number(row.customTotalSp) || 0;
                                            const gst = Number(row.customGST) || 0;
                                            const ppGST = pp * (1 + gst / 100);
                                            const spGST = sp * (1 + gst / 100);
                                            const mg = sp - pp;
                                            const pct = pp !== 0 ? ((mg / pp) * 100).toFixed(1) : '0.0';
                                            return (
                                                <SubtotalRow key={`custom-${i}`}>
                                                    <TableCell>{row.customLabel || '—'}</TableCell>
                                                    <TableCell colSpan={4} />
                                                    <TableCell sx={{ fontSize: 12, color: COLORS.textMuted }}>{gst}%</TableCell>
                                                    <TableCell colSpan={4} />
                                                    <TableCell>{formatCurrency(pp)}</TableCell>
                                                    <TableCell>{formatCurrency(sp)}</TableCell>
                                                    <TableCell sx={{ color: mg >= 0 ? COLORS.success : COLORS.danger }}>{formatCurrency(mg)}</TableCell>
                                                    <TableCell>{pct}%</TableCell>
                                                    <TableCell>{formatCurrency(ppGST)}</TableCell>
                                                    <TableCell>{formatCurrency(spGST)}</TableCell>
                                                    <TableCell colSpan={2} />
                                                </SubtotalRow>
                                            );
                                        })}

                                        {/* Subtotals */}
                                        {[
                                            ['Transportation', 'transportationTotalPp', 'transportationTotalSp', 'transportationGST'],
                                            ['Customization',  'customizationTotalPp',  'customizationTotalSp', 'customizationGST'],
                                            ['Marketing',      'marketingTotalPp',       'marketingTotalSp', 'marketingGST'],
                                        ].map(([label, ppKey, spKey,gstKey]) => {
                                            const pp = Number(selectedItem[ppKey]) || 0;
                                            const sp = Number(selectedItem[spKey]) || 0;
                                            const gst  = Number(selectedItem[gstKey]) || 0;
                                            const ppGST = pp * (1 + gst / 100);
                                            const spGST = sp * (1 + gst / 100);   
                                            const loss = sp < pp;
                                            return (
                                                <SubtotalRow key={label} sx={{ bgcolor: loss ? COLORS.dangerBg : 'inherit' }}>
                                                    <TableCell>{label}</TableCell>
                                                    <TableCell colSpan={4} />
                                                    <TableCell sx={{ fontSize: 12, color: COLORS.textMuted }}>{gst}%</TableCell>  {/* GST col */}
                                                    <TableCell colSpan={4}/>
                                                    <TableCell>{formatCurrency(pp)}</TableCell>
                                                    <TableCell>{formatCurrency(sp)}</TableCell>
                                                    <TableCell sx={{ color: loss ? COLORS.danger : COLORS.success }}>{formatCurrency(sp - pp)}</TableCell>
                                                    <TableCell>{pp !== 0 ? (((sp - pp) / pp) * 100).toFixed(1) : '0.0'} %</TableCell>
                                                    <TableCell>{formatCurrency(ppGST)}</TableCell>   {/* Total PP incl GST */}
                                                    <TableCell>{formatCurrency(spGST)}</TableCell>   {/* Total SP incl GST */}
                                                    <TableCell colSpan={2} />
                                                </SubtotalRow>
                                            );
                                        })}                                       

                                        {/* Grand total */}
                                        <TotalRow>
                                            <TableCell>Grand Total</TableCell>
                                            <TableCell colSpan={9} />
                                            {(() => {
                                                const transGST = Number(selectedItem.transportationGST) || 0;
                                                const custGST  = Number(selectedItem.customizationGST)  || 0;
                                                const mktGST   = Number(selectedItem.marketingGST)      || 0;
                                                // ── Define these FIRST so they're available for both grand totals and GST calc
                                                const transPP = Number(selectedItem.transportationTotalPp) || 0;
                                                const transSP = Number(selectedItem.transportationTotalSp) || 0;
                                                const custPP  = Number(selectedItem.customizationTotalPp)  || 0;
                                                const custSP  = Number(selectedItem.customizationTotalSp)  || 0;
                                                const mktPP   = Number(selectedItem.marketingTotalPp)      || 0;
                                                const mktSP   = Number(selectedItem.marketingTotalSp)      || 0;
                                                const linesGSTpp = selectedItem.items?.reduce((t, s) => t + (s.qty * s.pp * (1 + (s.gst || 0) / 100)), 0) || 0;
                                                const linesGSTsp = selectedItem.items?.reduce((t, s) => t + (s.qty * s.sp * (1 + (s.gst || 0) / 100)), 0) || 0;

                                                const customGSTPP = (selectedItem.customRows || []).reduce((t, r) => t + ((Number(r.customTotalPp) || 0) * (1 + (Number(r.customGST) || 0) / 100)), 0);
                                                const customGSTSP = (selectedItem.customRows || []).reduce((t, r) => t + ((Number(r.customTotalSp) || 0) * (1 + (Number(r.customGST) || 0) / 100)), 0);
                                                const grandPPGST = linesGSTpp + customGSTPP
                                                    + transPP * (1 + transGST / 100)
                                                    + custPP  * (1 + custGST  / 100)
                                                    + mktPP   * (1 + mktGST   / 100);

                                                const grandSPGST = linesGSTsp + customGSTSP
                                                    + transSP * (1 + transGST / 100)
                                                    + custSP  * (1 + custGST  / 100)
                                                    + mktSP   * (1 + mktGST   / 100);
                                                const grandPP = (selectedItem.items?.reduce((t, s) => t + (s.qty * s.pp), 0) || 0)
                                                    + (selectedItem.transportationTotalPp || 0) + (selectedItem.customizationTotalPp || 0) + (selectedItem.marketingTotalPp || 0) 
                                                    + (selectedItem.customRows || []).reduce((t, r) => t + (Number(r.customTotalPp) || 0), 0);
                                                const grandSP = (selectedItem.items?.reduce((t, s) => t + (s.qty * s.sp), 0) || 0)
                                                    + (selectedItem.transportationTotalSp || 0) + (selectedItem.customizationTotalSp || 0) + (selectedItem.marketingTotalSp || 0)
                                                    + (selectedItem.customRows || []).reduce((t, r) => t + (Number(r.customTotalSp) || 0), 0);
                                                const gm = grandSP - grandPP;
                                                const gmPct = grandPP !== 0 ? ((gm / grandPP) * 100).toFixed(1) : '0.0';
                                                return (
                                                    <>
                                                        <TableCell>{formatCurrency(grandPP)}</TableCell>
                                                        <TableCell>{formatCurrency(grandSP)}</TableCell>
                                                        <TableCell>{formatCurrency(gm)}</TableCell>
                                                        <TableCell>{gmPct} %</TableCell>
                                                        <TableCell>{formatCurrency(grandPPGST)}</TableCell>
                                                        <TableCell>{formatCurrency(grandSPGST)}</TableCell>
                                                        <TableCell colSpan={2} />
                                                    </>
                                                );
                                            })()}
                                        </TotalRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : (
                        <Typography sx={{ p: 4, textAlign: 'center' }} color="text.secondary">No item selected.</Typography>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, bgcolor: COLORS.white, borderTop: `1px solid ${COLORS.borderAlt}`, gap: 1 }}>
                    {canApprove && (
                        <>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => handleApproval(true)}
                                disabled={btnSpinner || modalLoading}
                                startIcon={btnSpinner ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                            >
                                {btnSpinner ? 'Approving…' : 'Approve'}
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setRemarkModal(true)}
                                disabled={btnSpinner || modalLoading}
                                startIcon={<CancelOutlinedIcon />}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                    <Button onClick={handleCloseModal} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', ml: 'auto' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Rejection remarks modal ───────────────────────────────────── */}
            <Dialog open={remarksModal} onClose={() => setRemarkModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${COLORS.borderAlt}` }}>
                    <Typography fontWeight={700} fontFamily={FONT.display}>Rejection Reason</Typography>
                    <IconButton size="small" onClick={() => setRemarkModal(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        fullWidth multiline rows={4}
                        value={remarkText}
                        onChange={e => setRemarkText(e.target.value)}
                        placeholder="Describe the reason for rejection…"
                        variant="outlined"
                        sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${COLORS.borderAlt}` }}>
                    <Button onClick={() => setRemarkModal(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        onClick={() => handleApproval(false)}
                        color="error"
                        variant="contained"
                        disabled={remarkText.trim() === '' || btnSpinner}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        {btnSpinner ? <CircularProgress size={18} color="inherit" /> : 'Confirm Rejection'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageShell>
    );
};

export default ApprovalDashboard;
